import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import moment from 'moment';
import { useIntl, useFilterManager, useAddAttachmentToAlbums } from 'hooks';
import { flashMessage, mapNodes } from 'helpers';
import ATTACHMENT_ALBUMS_QUERY from 'graphql/queries/attachmentAlbums.graphql';
import * as REMOVE_ATTACHMENTS_FROM_ATTACHMENT_ALBUM from 'graphql/mutations/removeAttachmentsFromAttachmentAlbum.graphql';
import RelayConnection from 'types/RelayConnection';
import Attachment from 'types/Attachment';
import MediaLibraryFilter from 'types/MediaLibraryFilter';
import AttachmentAlbum from 'types/AttachmentAlbum';
import Suggestion from 'types/Suggestion';
import Form from 'components/Form';

interface AlbumsProps {
  suggestions: Suggestion[];
}

interface QueryData {
  attachmentAlbums: RelayConnection<AttachmentAlbum>;
}

interface QueryVars {
  filter: Partial<MediaLibraryFilter>;
  first: null;
  cursor?: string;
}

interface MutationData {
  removeAttachmentsFromAttachmentAlbum: {
    success?: boolean;
    errors?: string;
  };
}

const defaultFilter: Readonly<MediaLibraryFilter> = {
  sources: [],
  mediaType: [],
  suggested: '2',
  query: '',
  publishers: [],
  submitters: [],
  suggesters: [],
  suggestionRecipients: [],
  attachmentAlbums: [],
  suggestionAlbums: [],
  suggesterPublishers: [],
  mediaAppearancesOnly: null,
  createdAt: {
    dateRange: 'all',
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  },
  canMonetize: false,
  uploaders: [],
};

function Albums({ suggestions }: AlbumsProps) {
  const { t } = useIntl();
  const [selectedAlbumIds, setSelectedAlbumIds] = useState<string[]>([]);
  const attachmentIds = suggestions.map(
    (suggestion) => suggestion.attachments[0]?.id
  );

  const { addToAlbum } = useAddAttachmentToAlbums(
    attachmentIds,
    selectedAlbumIds
  );

  const [removeAttachments] = useMutation<MutationData>(
    REMOVE_ATTACHMENTS_FROM_ATTACHMENT_ALBUM,
    {
      onCompleted: (data) => {
        if (data && data.removeAttachmentsFromAttachmentAlbum.success) {
          flashMessage('AlbumsMedia__DeleteMessage');
        } else {
          flashMessage('Global__UnexpectedError', { style: 'error' });
        }
      },
      refetchQueries: ['AttachmentAlbum'],
    }
  );

  const { debouncedFilter } = useFilterManager(defaultFilter, true);

  const { data, loading } = useQuery<QueryData, QueryVars>(
    ATTACHMENT_ALBUMS_QUERY,
    {
      // first: null fetches all data to be used in the select field.
      // It is not anticipated that users will have lots of albums
      // so the decision was made here not to rebuild the select field
      // to work with pagination and fetching more on scroll.
      variables: { filter: debouncedFilter, first: null },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );

  const attachmentAlbums: {
    id: number;
    name: string;
    attachments: RelayConnection<Attachment>;
  }[] = data?.attachmentAlbums ? mapNodes(data.attachmentAlbums.edges) : [];

  const selectOptions = attachmentAlbums.map((attachmentAlbum) => ({
    label: attachmentAlbum.name,
    value: attachmentAlbum.id,
  }));

  return (
    <div className="mb-3">
      <Form.Field>
        <Form.Label htmlFor="album-search" bold>
          {t('AlbumsMedia__AddToAlbum--AlbumSelectTitle')}
        </Form.Label>
        <Form.Select
          id="album-search"
          options={selectOptions}
          value={!loading && selectedAlbumIds}
          onChange={(e, action) => {
            if (action?.action === 'remove-value') {
              const removedValue = selectedAlbumIds.find(
                (id) => !e.includes(id)
              );
              if (removedValue) {
                removeAttachments({
                  variables: {
                    ids: attachmentIds,
                    albumId: removedValue,
                  },
                });
              }
            } else {
              addToAlbum({
                variables: { attachmentIds, attachmentAlbumIds: e },
              });
            }
            setSelectedAlbumIds(e);
          }}
          placeholder={t('AlbumsMedia__AddToAlbum--AlbumSelectTitle')}
          isMulti={true}
        />
      </Form.Field>
    </div>
  );
}

export default Albums;
